'use client';

import dynamic from 'next/dynamic';
import { SquarePlus } from 'lucide-react';
import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import type { Layout, Data, PlotRelayoutEvent, Config } from 'plotly.js';
import { MetricsLoadingOverlay } from '@/components/metrics-loading-overlay';
import { cn } from '@/lib/utils';
import { useChartContext } from '../ChartContext';
import { buildChartLegendRows, chartDatumToMs } from '../chart-legend';
import type { ChartLegendSortDirection } from '../chart-legend';

const PLOT_MARGIN = { top: 8, right: 16, bottom: 36, left: 56 };
const MIN_SHARED_METRICS_RANGE_MS = 30_000;
export const NON_INTERACTIVE_PLOT_CONFIG: Partial<Config> = {
  responsive: true,
  displayModeBar: false,
  staticPlot: true,
  scrollZoom: false,
  doubleClick: false,
};

const Plot = dynamic(
  async () => {
    const [{ default: createPlotlyComponent }, plotly] = await Promise.all([
      import('react-plotly.js/factory'),
      import('plotly.js-basic-dist-min'),
    ]);
    return createPlotlyComponent(plotly.default);
  },
  {
    ssr: false,
    loading: () => <div className="h-full w-full animate-pulse rounded-md bg-muted/30" />,
  },
);

interface PlotlyChartProps {
  // Per-pod charts MUST set `name` to the pod id so visibility from the
  // shared legend can target the right trace. perPod=false skips the
  // color/visibility merge (used by InfraProbes which is keyed by probe).
  traces: Data[];
  xTitle?: string;
  xType?: 'date' | 'linear' | 'category';
  yTitle?: string;
  legendValueLabel?: string;
  legendSortDirection?: ChartLegendSortDirection;
  yRange?: [number, number];
  shapes?: Layout['shapes'];
  height?: number;
  perPod?: boolean;
  showLegend?: boolean;
  hiddenSeries?: Set<string>;
  onToggleSeries?: (name: string) => void;
  onIsolateSeries?: (name: string) => void;
  onAddClientGroup?: (client: string) => void;
  loading?: boolean;
  timeAxis?: boolean;
  layoutOverrides?: Partial<Layout>;
}

export function CursorLegendTable({
  chartId,
  traces,
  unit,
  valueLabel,
  sortDirection = 'desc',
  onToggle,
  onIsolate,
  onAddClientGroup,
}: {
  chartId: string;
  traces: Data[];
  unit?: string;
  valueLabel?: string;
  sortDirection?: ChartLegendSortDirection;
  onToggle: (name: string) => void;
  onIsolate: (name: string) => void;
  onAddClientGroup?: (client: string) => void;
}) {
  const { hoverBus } = useChartContext();
  const [cursorMs, setCursorMs] = useState<number | null>(null);

  useEffect(() => {
    const id = `${chartId}-legend`;
    hoverBus.register(id, { onCursor: setCursorMs });
    return () => hoverBus.unregister(id);
  }, [chartId, hoverBus]);

  const rows = useMemo(
    () => buildChartLegendRows(traces, cursorMs, sortDirection),
    [cursorMs, sortDirection, traces],
  );
  if (rows.length === 0) return null;
  const cursorHeading = cursorMs === null
    ? 'Latest'
    : new Date(cursorMs).toLocaleTimeString(undefined, { hour12: false });
  const valueHeading = valueLabel ?? cursorHeading;
  const workloadColumns = rows.find((row) => row.legendParts && row.legendParts.length > 0)
    ?.legendParts?.map(({ key, label }) => ({ key, label })) ?? null;
  const firstWorkloadColumn = workloadColumns?.[0]?.key;
  const compactColumnLabels: Record<string, string> = {
    resourceType: 'Resource',
    apiLabel: 'API',
  };
  const selectSeries = (event: ReactMouseEvent<HTMLButtonElement>, name: string) => {
    if (event.metaKey || event.ctrlKey) {
      onToggle(name);
      return;
    }
    onIsolate(name);
  };

  return (
    <div className="mt-1 max-h-28 overflow-auto border-t">
      <table className="w-full table-fixed text-[10px]">
        {workloadColumns ? (
          <colgroup>
            <col />
            <col className="w-[66px]" />
            <col className="w-[40px]" />
            <col className="w-[58px]" />
            <col className={valueLabel === 'Concurrency'
              ? 'w-[68px]'
              : valueLabel === 'RPS' ? 'w-[48px]' : 'w-[54px]'} />
          </colgroup>
        ) : null}
        <thead className="sticky top-0 z-10 bg-card text-muted-foreground">
          <tr>
            {workloadColumns ? workloadColumns.map((column) => (
              <th
                key={column.key}
                scope="col"
                title={compactColumnLabels[column.key] ? column.label : undefined}
                className={column.key === 'username'
                  ? 'px-1 py-1 text-left font-medium'
                  : column.key === 'resourceType'
                    ? 'whitespace-nowrap px-0 py-1 text-left font-medium'
                  : column.key === 'action'
                    ? 'whitespace-nowrap py-1 pr-1 pl-0 text-left font-medium'
                    : 'whitespace-nowrap px-0.5 py-1 text-left font-medium'}
              >
                {compactColumnLabels[column.key] ?? column.label}
              </th>
            )) : (
              <th scope="col" className="w-[72%] px-1.5 py-1 text-left font-medium">Series</th>
            )}
            <th
              scope="col"
              title={unit ? `${cursorHeading} (${unit})` : cursorHeading}
              className="whitespace-nowrap px-0.5 py-1 text-right font-medium"
            >
              {valueHeading}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.name} className={row.hidden ? 'opacity-35' : undefined}>
              {workloadColumns ? workloadColumns.map((column) => {
                const part = row.legendParts?.find((candidate) => candidate.key === column.key);
                const isFirst = column.key === firstWorkloadColumn;
                return (
                  <td
                    key={column.key}
                    className={column.key === 'username'
                      ? 'max-w-0 px-1 py-0.5'
                      : column.key === 'resourceType'
                        ? 'whitespace-nowrap px-0 py-0.5 font-mono'
                      : column.key === 'action'
                        ? 'whitespace-nowrap py-0.5 pr-1 pl-0 font-mono'
                        : 'whitespace-nowrap px-0.5 py-0.5 font-mono'}
                  >
                    <div className="flex min-w-0 items-center gap-0.5">
                      {isFirst ? (
                        <button
                          type="button"
                          onClick={(event) => selectSeries(event, row.name)}
                          title={`Click: isolate ${row.name} · Cmd/Ctrl-click: add or remove`}
                          className="flex min-w-0 flex-1 items-center gap-1 text-left hover:text-foreground"
                        >
                          <span className="h-[3px] w-3 shrink-0 rounded-sm" style={{ background: row.color }} />
                          <span
                            title={column.key === 'username' ? part?.value : undefined}
                            className={column.key === 'username'
                              ? 'block min-w-0 overflow-hidden text-ellipsis whitespace-nowrap text-left font-mono [direction:rtl]'
                              : 'whitespace-nowrap font-mono'}
                          >
                            {part?.value ?? '—'}
                          </span>
                        </button>
                      ) : <span className="whitespace-nowrap font-mono">{part?.value ?? '—'}</span>}
                      {column.key === 'username' && onAddClientGroup && part && (
                        <button
                          type="button"
                          onClick={() => onAddClientGroup(part.value)}
                          title={`Add chart group for ${part.value}`}
                          aria-label={`Add chart group for client ${part.value}`}
                          className="shrink-0 rounded-sm p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                        >
                          <SquarePlus className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                  </td>
                );
              }) : (
                <td className="min-w-0 px-1.5 py-0.5">
                  <button
                    type="button"
                    onClick={(event) => selectSeries(event, row.name)}
                    title={`Click: isolate ${row.name} · Cmd/Ctrl-click: add or remove`}
                    className="flex w-full min-w-0 items-center gap-1.5 text-left hover:text-foreground"
                  >
                    <span className="h-[3px] w-3.5 shrink-0 rounded-sm" style={{ background: row.color }} />
                    <span className="truncate font-mono">{row.name}</span>
                  </button>
                </td>
              )}
              <td className={workloadColumns
                ? 'px-0.5 py-0.5 text-right font-mono tabular-nums'
                : 'px-1.5 py-0.5 text-right font-mono tabular-nums'}>
                {row.value === null
                  ? '—'
                  : row.value.toLocaleString(undefined, { maximumFractionDigits: 1 })}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function cursorTimestamp(
  element: HTMLElement | null,
  xPixel: number,
  xRatio: number,
  traces: Data[],
): number | null {
  const axis = (element as (HTMLElement & {
    _fullLayout?: { xaxis?: { p2d?: (pixel: number) => unknown } };
  }) | null)?._fullLayout?.xaxis;
  const plotlyValue = axis?.p2d?.(xPixel);
  const plotlyTimestamp = chartDatumToMs(plotlyValue);
  if (plotlyTimestamp !== null) return plotlyTimestamp;

  let fromMs = Number.POSITIVE_INFINITY;
  let toMs = Number.NEGATIVE_INFINITY;
  for (const trace of traces) {
    for (const value of Array.from((trace as { x?: Iterable<unknown> }).x ?? [])) {
      const timestamp = chartDatumToMs(value);
      if (timestamp === null) continue;
      fromMs = Math.min(fromMs, timestamp);
      toMs = Math.max(toMs, timestamp);
    }
  }
  if (!Number.isFinite(fromMs) || !Number.isFinite(toMs)) return null;
  return fromMs + (toMs - fromMs) * xRatio;
}

export function snapRangeToTraceTimestamps(
  fromMs: number,
  toMs: number,
  traces: Data[],
  minimumRangeMs = 0,
): [number, number] {
  const timestamps = Array.from(new Set(
    traces.flatMap((trace) => Array.from((trace as { x?: Iterable<unknown> }).x ?? [])
      .map(chartDatumToMs)
      .filter((value): value is number => value !== null)),
  )).sort((left, right) => left - right);

  if (timestamps.length === 0) return [fromMs, toMs];

  const previousIndex = timestamps.findLastIndex((timestamp) => timestamp <= fromMs);
  const nextIndex = timestamps.findIndex((timestamp) => timestamp >= toMs);
  let startIndex = previousIndex >= 0 ? previousIndex : 0;
  let endIndex = nextIndex >= 0 ? nextIndex : timestamps.length - 1;

  // A line chart needs two timestamps to remain visibly useful. This also
  // handles a drag made in the small empty margin before/after the samples.
  if (startIndex === endIndex && timestamps.length > 1) {
    if (endIndex < timestamps.length - 1) endIndex += 1;
    else startIndex -= 1;
  }

  let snappedFromMs = Math.min(fromMs, timestamps[startIndex]);
  let snappedToMs = Math.max(toMs, timestamps[endIndex]);
  if (snappedToMs - snappedFromMs < minimumRangeMs) {
    const midpointMs = (snappedFromMs + snappedToMs) / 2;
    snappedFromMs = midpointMs - minimumRangeMs / 2;
    snappedToMs = midpointMs + minimumRangeMs / 2;
  }

  return [snappedFromMs, snappedToMs];
}

export function PlotlyChart({
  traces,
  xTitle,
  xType = 'date',
  yTitle,
  legendValueLabel,
  legendSortDirection,
  yRange,
  shapes,
  height = 250,
  perPod = true,
  hiddenSeries,
  onToggleSeries,
  onIsolateSeries,
  onAddClientGroup,
  loading = false,
  timeAxis = true,
  layoutOverrides,
  showLegend = true,
}: PlotlyChartProps) {
  const { hiddenPods, podColors, setHidden, isolatePod, zoomBus, hoverBus } = useChartContext();
  const chartId = useId();
  const elRef = useRef<HTMLElement | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const verticalCrosshairRef = useRef<HTMLDivElement | null>(null);
  const horizontalCrosshairRef = useRef<HTMLDivElement | null>(null);
  const timestampRef = useRef<HTMLDivElement | null>(null);
  const rangeSelectionRef = useRef<HTMLDivElement | null>(null);
  const dragRef = useRef<{ pointerId: number; startX: number; currentX: number; moved: boolean } | null>(null);
  const suppressNextClickRef = useRef(false);
  // Only honor a captured range AFTER the user (or a bus echo) has actually
  // zoomed. Otherwise the auto-computed range from onInitialized/onUpdate
  // would pin the view to whatever Plotly first picked — including the
  // empty-data range from the initial mount, which hides incoming samples.
  const [pinnedRange, setPinnedRange] = useState<[unknown, unknown] | null>(
    () => timeAxis ? zoomBus.getCurrentRange() : null,
  );
  const [locallyHidden, setLocallyHidden] = useState<Set<string>>(new Set());

  // Fresh trace objects every render — react-plotly.js may mutate them.
  const data: Data[] = perPod
    ? traces.map((t) => {
        const name = (t as { name?: string }).name ?? '';
        const color = podColors[name];
        const visible: true | 'legendonly' = hiddenPods.has(name) ? 'legendonly' : true;
        const line = (t as { line?: { width?: number; color?: string } }).line ?? {};
        return {
          ...t,
          line: { ...line, color: color ?? line.color ?? '#888', width: line.width ?? 1.5 },
          legendgroup: name,
          visible,
        } as Data;
      })
    : traces.map((t) => {
        const name = String((t as { name?: unknown }).name ?? '');
        return {
          ...t,
          visible: (hiddenSeries ?? locallyHidden).has(name) ? 'legendonly' : true,
        } as Data;
      });

  // Build the chart's "static" layout (look & feel only). The xaxis range
  // is grafted on from liveLayoutRef so Plotly.react() preserves the user's
  // current zoom across data updates.
  const baseLayout: Partial<Layout> = {
    autosize: true,
    height,
    paper_bgcolor: 'transparent',
    plot_bgcolor: 'transparent',
    font: {
      color: 'hsl(var(--muted-foreground))',
      size: 11,
      family: 'ui-monospace, SFMono-Regular, "JetBrains Mono", Menlo, monospace',
    },
    margin: { t: PLOT_MARGIN.top, b: PLOT_MARGIN.bottom, l: PLOT_MARGIN.left, r: PLOT_MARGIN.right },
    dragmode: false,
    xaxis: {
      type: xType,
      title: xTitle ? { text: xTitle, font: { size: 11 } } : undefined,
      tickformat: timeAxis ? '%H:%M:%S' : undefined,
      gridcolor: 'hsl(var(--border))',
      fixedrange: true,
    },
    yaxis: {
      title: yTitle ? { text: yTitle, font: { size: 11 } } : undefined,
      gridcolor: 'hsl(var(--border))',
      zeroline: false,
      fixedrange: true,
      ...(yRange ? { range: yRange, autorange: false } : {}),
    },
    showlegend: false,
    hovermode: timeAxis ? false : 'closest',
    shapes,
  };

  // Preserve a range applied by our shared window controls across data
  // updates. Plotly's own pan and zoom interactions are disabled.
  if (timeAxis && pinnedRange) {
    const x = baseLayout.xaxis as Record<string, unknown>;
    x.range = [...pinnedRange];
    x.autorange = false;
  }
  const layout: Partial<Layout> = {
    ...baseLayout,
    ...layoutOverrides,
    xaxis: { ...baseLayout.xaxis, ...layoutOverrides?.xaxis },
    yaxis: { ...baseLayout.yaxis, ...layoutOverrides?.yaxis },
  };

  const onInitialized = (_fig: unknown, gd: Readonly<HTMLElement>) => {
    const el = gd as HTMLElement;
    elRef.current = el;
    if (timeAxis) zoomBus.register(chartId, el, setPinnedRange);
  };

  useEffect(() => {
    if (!timeAxis) return;
    return () => zoomBus.unregister(chartId);
  }, [chartId, timeAxis, zoomBus]);

  useEffect(() => {
    const vertical = verticalCrosshairRef.current;
    const horizontal = horizontalCrosshairRef.current;
    const timestamp = timestampRef.current;
    if (!timeAxis || !vertical || !horizontal || !timestamp) return;
    hoverBus.register(chartId, { vertical, horizontal, timestamp });
    return () => hoverBus.unregister(chartId);
  }, [chartId, hoverBus, timeAxis]);

  const onRelayout = (ed: Readonly<PlotRelayoutEvent>) => {
    const e = ed as Record<string, unknown>;
    const bus = zoomBus as typeof zoomBus & { _isSuppressed?: (id: string) => boolean };

    // Only ranges sent by our shared controls may affect a chart. Ignore any
    // other Plotly relayout event, including native interaction attempts.
    if (!bus._isSuppressed?.(chartId)) return;

    // Keep the controlled range through subsequent React renders.
    if (e['xaxis.autorange']) {
      setPinnedRange(null);
    } else if (e['xaxis.range[0]'] !== undefined && e['xaxis.range[1]'] !== undefined) {
      setPinnedRange([e['xaxis.range[0]'], e['xaxis.range[1]']]);
    } else if (Array.isArray(e['xaxis.range'])) {
      const r = e['xaxis.range'] as unknown[];
      setPinnedRange([r[0], r[1]]);
    }

  };

  const onMouseMove = (event: ReactMouseEvent<HTMLDivElement>) => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    const rect = wrapper.getBoundingClientRect();
    const plotWidth = rect.width - PLOT_MARGIN.left - PLOT_MARGIN.right;
    const plotHeight = rect.height - PLOT_MARGIN.top - PLOT_MARGIN.bottom;
    const x = event.clientX - rect.left - PLOT_MARGIN.left;
    const y = event.clientY - rect.top - PLOT_MARGIN.top;
    if (x < 0 || y < 0 || x > plotWidth || y > plotHeight) {
      hoverBus.clear();
      return;
    }
    const xRatio = x / plotWidth;
    hoverBus.broadcast(xRatio, y / plotHeight, cursorTimestamp(elRef.current, x, xRatio, data));
  };

  const plotPosition = (clientX: number, clientY: number) => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return null;
    const rect = wrapper.getBoundingClientRect();
    const width = rect.width - PLOT_MARGIN.left - PLOT_MARGIN.right;
    const height = rect.height - PLOT_MARGIN.top - PLOT_MARGIN.bottom;
    const x = clientX - rect.left - PLOT_MARGIN.left;
    const y = clientY - rect.top - PLOT_MARGIN.top;
    if (x < 0 || y < 0 || x > width || y > height) return null;
    return { x, width };
  };

  const updateRangeSelection = (startX: number, currentX: number) => {
    const selection = rangeSelectionRef.current;
    if (!selection) return;
    selection.style.left = `${Math.min(startX, currentX)}px`;
    selection.style.width = `${Math.abs(currentX - startX)}px`;
    selection.style.display = 'block';
  };

  const clearRangeSelection = () => {
    if (rangeSelectionRef.current) rangeSelectionRef.current.style.display = 'none';
  };

  const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) return;
    const position = plotPosition(event.clientX, event.clientY);
    if (!position) return;
    dragRef.current = { pointerId: event.pointerId, startX: position.x, currentX: position.x, moved: false };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const onPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    const rect = wrapper.getBoundingClientRect();
    const plotWidth = rect.width - PLOT_MARGIN.left - PLOT_MARGIN.right;
    drag.currentX = Math.max(0, Math.min(plotWidth, event.clientX - rect.left - PLOT_MARGIN.left));
    if (!drag.moved && Math.abs(drag.currentX - drag.startX) >= 4) {
      drag.moved = true;
      hoverBus.unpin();
    }
    if (drag.moved) {
      event.preventDefault();
      updateRangeSelection(drag.startX, drag.currentX);
    }
  };

  const finishRangeSelection = (event: ReactPointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    dragRef.current = null;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    clearRangeSelection();
    if (!drag.moved) return;

    suppressNextClickRef.current = true;
    window.setTimeout(() => { suppressNextClickRef.current = false; }, 0);
    const wrapperWidth = wrapperRef.current?.getBoundingClientRect().width ?? 0;
    const plotWidth = Math.max(1, wrapperWidth - PLOT_MARGIN.left - PLOT_MARGIN.right);
    const startRatio = drag.startX / plotWidth;
    const endRatio = drag.currentX / plotWidth;
    const startMs = cursorTimestamp(elRef.current, drag.startX, startRatio, data);
    const endMs = cursorTimestamp(elRef.current, drag.currentX, endRatio, data);
    if (startMs === null || endMs === null || startMs === endMs) return;
    const [fromMs, toMs] = snapRangeToTraceTimestamps(
      Math.min(startMs, endMs),
      Math.max(startMs, endMs),
      data,
      MIN_SHARED_METRICS_RANGE_MS,
    );
    const from = new Date(fromMs).toISOString();
    const to = new Date(toMs).toISOString();
    setPinnedRange([from, to]);
    zoomBus.broadcast(chartId, { 'xaxis.range[0]': from, 'xaxis.range[1]': to });
  };

  const cancelRangeSelection = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (dragRef.current?.pointerId !== event.pointerId) return;
    dragRef.current = null;
    clearRangeSelection();
  };

  const onChartClick = (event: ReactMouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    if (suppressNextClickRef.current) {
      suppressNextClickRef.current = false;
      return;
    }
    if (hoverBus.isPinned()) {
      hoverBus.unpin();
      onMouseMove(event);
      return;
    }
    hoverBus.pin();
  };

  const toggleSeries = (name: string) => {
    if (perPod) {
      setHidden(name, !hiddenPods.has(name));
      return;
    }
    if (onToggleSeries) {
      onToggleSeries(name);
      return;
    }
    setLocallyHidden((current) => {
      const next = new Set(current);
      if (next.has(name)) next.delete(name); else next.add(name);
      return next;
    });
  };

  const isolateSeries = (name: string) => {
    if (perPod) {
      isolatePod(name);
      return;
    }
    if (onIsolateSeries) {
      onIsolateSeries(name);
      return;
    }
    setLocallyHidden((current) => {
      const names = traces.map((trace) => String((trace as { name?: unknown }).name ?? ''));
      const onlyThisVisible = current.size === names.length - 1 && !current.has(name);
      return onlyThisVisible ? new Set() : new Set(names.filter((candidate) => candidate !== name));
    });
  };

  return (
    <div>
      <MetricsLoadingOverlay loading={loading}>
        <div
          ref={wrapperRef}
          className={cn('relative select-none', timeAxis ? 'cursor-crosshair' : 'cursor-default')}
          style={{ height }}
          onMouseEnter={timeAxis ? (event) => {
            hoverBus.enterChart();
            onMouseMove(event);
          } : undefined}
          onMouseMoveCapture={timeAxis ? onMouseMove : undefined}
          onPointerDownCapture={timeAxis ? onPointerDown : undefined}
          onPointerMoveCapture={timeAxis ? onPointerMove : undefined}
          onPointerUpCapture={timeAxis ? finishRangeSelection : undefined}
          onPointerCancelCapture={timeAxis ? cancelRangeSelection : undefined}
          onMouseLeave={timeAxis ? (event) => {
            const rect = wrapperRef.current?.getBoundingClientRect();
            if (rect && event.clientX >= rect.left && event.clientX <= rect.right
              && event.clientY >= rect.top && event.clientY <= rect.bottom) return;
            hoverBus.leaveChart();
          } : undefined}
          onClickCapture={timeAxis ? onChartClick : undefined}
        >
          <Plot
            data={data}
            layout={layout}
            config={timeAxis ? NON_INTERACTIVE_PLOT_CONFIG : { ...NON_INTERACTIVE_PLOT_CONFIG, staticPlot: false }}
            useResizeHandler
            style={{ width: '100%', height }}
            onInitialized={onInitialized}
            onRelayout={timeAxis ? onRelayout : undefined}
          />
          {timeAxis && (
            <div
              className="pointer-events-none absolute z-20"
              style={{
                top: PLOT_MARGIN.top,
                right: PLOT_MARGIN.right,
                bottom: PLOT_MARGIN.bottom,
                left: PLOT_MARGIN.left,
              }}
              aria-hidden="true"
            >
              <div data-crosshair="vertical" ref={verticalCrosshairRef} className="absolute inset-y-0 hidden w-px -translate-x-1/2 bg-foreground/60" />
              <div data-crosshair="horizontal" ref={horizontalCrosshairRef} className="absolute inset-x-0 hidden h-px -translate-y-1/2 bg-foreground/60" />
              <div
                ref={timestampRef}
                data-cursor-timestamp
                className="absolute z-10 mt-0.5 hidden whitespace-nowrap rounded-sm border bg-popover px-1.5 py-0.5 font-mono text-[10px] leading-none text-popover-foreground shadow-sm"
                style={{ top: 'calc(100% + 16px)' }}
              />
              <div
                ref={rangeSelectionRef}
                data-range-selection
                className="absolute inset-y-0 hidden border-x border-primary bg-primary/15"
              />
            </div>
          )}
        </div>
        {timeAxis && <div data-cursor-timestamp-spacer className="h-2" aria-hidden="true" />}
      </MetricsLoadingOverlay>
      {showLegend && (
        <div className={cn('transition-opacity duration-150', loading && 'pointer-events-none opacity-50')}>
          <CursorLegendTable
            chartId={chartId}
            traces={data}
            unit={yTitle}
            valueLabel={legendValueLabel}
            sortDirection={legendSortDirection}
            onToggle={toggleSeries}
            onIsolate={isolateSeries}
            onAddClientGroup={onAddClientGroup}
          />
        </div>
      )}
    </div>
  );
}
